import apiClient from './apiClient';

const serviceTicketService = {
    /**
     * Get all service tickets with optional filters
     */
    getAllTickets: async (filters = {}) => {
        try {
            const params = new URLSearchParams();
            Object.keys(filters).forEach(key => {
                if (filters[key]) params.append(key, filters[key]);
            });

            const response = await apiClient.get(`/service/tickets?${params.toString()}`);
            return response;
        } catch (error) {
            console.error('Error fetching service tickets:', error);
            throw error;
        }
    },

    /**
     * Get single ticket with full details
     */
    getTicketById: async (ticketId) => {
        try {
            const response = await apiClient.get(`/service/tickets/${ticketId}`);
            return response;
        } catch (error) {
            console.error(`Error fetching ticket ${ticketId}:`, error);
            throw error;
        }
    },

    /**
     * Create new service ticket
     */
    createTicket: async (ticketData) => {
        try {
            const response = await apiClient.post('/service/tickets', ticketData);
            return response;
        } catch (error) {
            console.error('Error creating service ticket:', error);
            throw error;
        }
    },

    /**
     * Update ticket status
     */
    updateTicketStatus: async (ticketId, status, notes = '') => {
        try {
            const response = await apiClient.patch(`/service/tickets/${ticketId}/status`, {
                status,
                notes
            });
            return response;
        } catch (error) {
            console.error(`Error updating ticket ${ticketId} status:`, error);
            throw error;
        }
    },

    /**
     * Add log entry to ticket
     */
    addLogEntry: async (ticketId, logData) => {
        try {
            const response = await apiClient.post(`/service/tickets/${ticketId}/logs`, logData);
            return response;
        } catch (error) {
            console.error(`Error adding log to ticket ${ticketId}:`, error);
            throw error;
        }
    },

    /**
     * Assign technician to ticket
     */
    assignTechnician: async (ticketId, technicianId, role = 'primary') => {
        try {
            const response = await apiClient.post(`/service/tickets/${ticketId}/assign`, {
                technician_id: technicianId,
                role
            });
            return response;
        } catch (error) {
            console.error(`Error assigning technician to ticket ${ticketId}:`, error);
            throw error;
        }
    },

    /**
     * Requisition parts for ticket
     */
    requisitionParts: async (ticketId, partsData) => {
        try {
            const response = await apiClient.post(`/service/tickets/${ticketId}/parts`, partsData);
            return response;
        } catch (error) {
            console.error(`Error requisitioning parts for ticket ${ticketId}:`, error);
            throw error;
        }
    },

    /**
     * Clock in for work session
     */
    clockIn: async (ticketId, technicianId) => {
        try {
            const response = await apiClient.post(`/service/tickets/${ticketId}/time/clock-in`, {
                technician_id: technicianId
            });
            return response;
        } catch (error) {
            console.error(`Error clocking in for ticket ${ticketId}:`, error);
            throw error;
        }
    },

    /**
     * Clock out from work session
     */
    clockOut: async (ticketId, technicianId, breakDuration = 0, notes = '') => {
        try {
            const response = await apiClient.post(`/service/tickets/${ticketId}/time/clock-out`, {
                technician_id: technicianId,
                break_duration: breakDuration,
                notes
            });
            return response;
        } catch (error) {
            console.error(`Error clocking out for ticket ${ticketId}:`, error);
            throw error;
        }
    },

    /**
     * Get service analytics
     */
    getAnalytics: async (dateFrom, dateTo) => {
        try {
            const params = new URLSearchParams();
            if (dateFrom) params.append('date_from', dateFrom);
            if (dateTo) params.append('date_to', dateTo);

            const response = await apiClient.get(`/service/analytics?${params.toString()}`);
            return response;
        } catch (error) {
            console.error('Error fetching service analytics:', error);
            throw error;
        }
    }
};

export default serviceTicketService;
